/**
 * support_issue — an Issue a signed-in user reported from inside Staff (2026-10-19).
 *
 * Replaces the Delyver feedback widget as the way in. Staff keeps its own record — the
 * marked-up screenshot, the captured context, the queue the ADMIN role works — and pushes a
 * copy of each Issue ONE WAY to Delyver (`delyver_*` columns). Status changes made in
 * Delyver do not come back.
 *
 *   Workflow: Open → In Progress → In Review → Blocked → Resolved → Closed
 *   Active:   everything except Resolved and Closed.
 *
 * Comments and every change live in `audit_log` (entity_type 'support_issue'), which is
 * append-only; the Issue's timeline is built from those rows.
 *
 * The reporter is snapshotted (name, email, role) as they were at submit, so the queue
 * still says who reported it after a role change or a deleted user.
 *
 * Screenshot files live in S3 under `issues/<id>/`. `screenshot_key` is the marked-up
 * image (or the plain one when the reporter drew nothing); `original_screenshot_key` is the
 * un-marked capture, kept for ADMIN only, and never kept when the reporter used Redact.
 * Where file storage is off, the screenshot still goes to Delyver but is not kept here —
 * `screenshot_state` says which.
 */

import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgTable as table,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { user } from "./auth";

export const SUPPORT_ISSUE_STATUSES = [
  "open",
  "in_progress",
  "in_review",
  "blocked",
  "resolved",
  "closed",
] as const;

export const SUPPORT_ISSUE_PRIORITIES = ["low", "normal", "high", "urgent"] as const;

export const SUPPORT_ISSUE_CATEGORIES = ["bug", "question", "data", "feature"] as const;

/** none: no screenshot. stored: in S3. delyver_only: sent to Delyver, storage off here. */
export const SUPPORT_ISSUE_SCREENSHOT_STATES = ["none", "stored", "delyver_only"] as const;

/** pending: not tried yet. sent: Delyver has it. failed: will retry. skipped: no key here. */
export const DELYVER_SYNC_STATUSES = ["pending", "sent", "failed", "skipped"] as const;

export const supportIssue = table(
  "support_issue",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    /** ISS-XXXXXX — random, not sequential, so it does not reveal volume. */
    code: text("code").notNull(),
    /**
     * Generated by the form when it opens. A retried submit carries the same value and
     * gets the Issue it already made back — one press, one Issue.
     */
    clientSubmissionId: text("client_submission_id").notNull(),

    title: text("title").notNull(),
    description: text("description").notNull(),
    category: text("category"),
    priority: text("priority").notNull().default("normal"),
    status: text("status").notNull().default("open"),

    reporterUserId: text("reporter_user_id").references(() => user.id, { onDelete: "set null" }),
    reporterName: text("reporter_name").notNull(),
    reporterEmail: text("reporter_email").notNull(),
    reporterRole: text("reporter_role").notNull(),
    /** One named ADMIN, or nobody. */
    ownerUserId: text("owner_user_id").references(() => user.id, { onDelete: "set null" }),

    // Where it happened — captured, never typed.
    pageUrl: text("page_url"),
    route: text("route"),
    userAgent: text("user_agent"),
    viewport: text("viewport"),
    appVersion: text("app_version"),
    /** Last console errors/warnings, scrubbed and capped server-side. ADMIN only. */
    consoleTrail: jsonb("console_trail").notNull().default(sql`'[]'::jsonb`),
    /** Last API calls and navigations (method, path, status, ms — no bodies). ADMIN only. */
    networkTrail: jsonb("network_trail").notNull().default(sql`'[]'::jsonb`),

    screenshotState: text("screenshot_state").notNull().default("none"),
    screenshotKey: text("screenshot_key"),
    screenshotContentType: text("screenshot_content_type"),
    originalScreenshotKey: text("original_screenshot_key"),
    originalScreenshotContentType: text("original_screenshot_content_type"),
    /** The reporter drew on the screenshot before submitting. */
    screenshotMarkedUp: boolean("screenshot_marked_up").notNull().default(false),
    /** The reporter blacked something out — the un-marked original was never kept. */
    screenshotRedacted: boolean("screenshot_redacted").notNull().default(false),
    /** The text notes typed on the screenshot, in order — for Copy for Claude Code. */
    screenshotNotes: jsonb("screenshot_notes").notNull().default(sql`'[]'::jsonb`),

    // One-way push to Delyver.
    delyverSyncStatus: text("delyver_sync_status").notNull().default("pending"),
    delyverShortId: text("delyver_short_id"),
    delyverAttempts: integer("delyver_attempts").notNull().default(0),
    delyverLastError: text("delyver_last_error"),
    delyverSyncedAt: timestamp("delyver_synced_at", { withTimezone: true, mode: "date" }),
    /** The Delyver-sized copy of the screenshot, kept until the push succeeds. */
    delyverImageKey: text("delyver_image_key"),
    delyverImageContentType: text("delyver_image_content_type"),

    resolvedAt: timestamp("resolved_at", { withTimezone: true, mode: "date" }),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "date" }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "date" }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("support_issue_code_key").on(t.code),
    uniqueIndex("support_issue_client_submission_key").on(t.clientSubmissionId),
    check(
      "support_issue_title_valid",
      sql`length(btrim(${t.title})) > 0 AND length(${t.title}) <= 200`,
    ),
    check(
      "support_issue_description_valid",
      sql`length(btrim(${t.description})) > 0 AND length(${t.description}) <= 5000`,
    ),
    check(
      "support_issue_status_valid",
      sql`${t.status} IN ('open', 'in_progress', 'in_review', 'blocked', 'resolved', 'closed')`,
    ),
    check(
      "support_issue_priority_valid",
      sql`${t.priority} IN ('low', 'normal', 'high', 'urgent')`,
    ),
    check(
      "support_issue_category_valid",
      sql`${t.category} IS NULL OR ${t.category} IN ('bug', 'question', 'data', 'feature')`,
    ),
    check(
      "support_issue_screenshot_state_valid",
      sql`${t.screenshotState} IN ('none', 'stored', 'delyver_only')`,
    ),
    check(
      "support_issue_delyver_sync_status_valid",
      sql`${t.delyverSyncStatus} IN ('pending', 'sent', 'failed', 'skipped')`,
    ),
    // A redacted screenshot never keeps its un-marked original.
    check(
      "support_issue_redacted_has_no_original",
      sql`NOT ${t.screenshotRedacted} OR ${t.originalScreenshotKey} IS NULL`,
    ),
    check(
      "support_issue_resolved_has_stamp",
      sql`(${t.status} IN ('resolved', 'closed')) = (${t.resolvedAt} IS NOT NULL)`,
    ),
    // The queue: newest first, cursor-paginated.
    index("support_issue_created_at_idx").on(t.createdAt.desc(), t.id),
    // "My issues" for a reporter.
    index("support_issue_reporter_idx").on(t.reporterUserId, t.createdAt.desc()),
    index("support_issue_owner_idx").on(t.ownerUserId),
    // The Delyver retry sweep (`skipped` is retried once a key is configured).
    index("support_issue_delyver_unsent_idx")
      .on(t.createdAt)
      .where(sql`${t.delyverSyncStatus} IN ('pending', 'failed', 'skipped')`),
  ],
);
